// src/components/CheckoutMatch.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Divider,
  Typography,
  CircularProgress,
  Link,
  Stack,
  // Image,
  useMediaQuery,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import AppleIcon from '@mui/icons-material/Apple';
import { CardElement, useRecurly } from '@recurly/react-recurly';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import AdvisoryCloudLogo from '../../components/AdvisoryCloudLogo';
import TrustPilotBanner from '../../components/TrustPilotBanner';
import { signUp, getRecurlyPlanOpen } from '../../services/Backend/account';
import screen from './screen.png';
import person1 from './person1.png';
import person2 from './person2.png';
import TestimonialCard from './TestimonialCard';
import StepsBanner from './StepsBanner';

const bullets = [
  'Guaranteed board placement given our exclusive relationships.',
  'Free board matching and setup call to optimize your Advisor Profile and Advisor Rate.',
  'Leverage your Advisor Profile to connect with new professional opportunities including project based advisory work.',
];

const testimonials = [
  {
    description:
      'After a short time using their platform, I have been placed in an advisory board role! The platform is easy to navigate.',
    name: 'Tammy Martin',
    title: 'CEO & Advisor',
    image: person2,
  },
  {
    description:
      'AdvisoryCloud helped me find new and exciting advisory work with a company I wouldn’t have met otherwise.',
    name: 'Danny Stroud',
    title: 'COO, Neptune Mobile',
    image: person1,
  },
];

const CheckoutMatch = () => {
  const location = useLocation();
  const recurly = useRecurly();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [planData, setPlanData] = useState(null);
  const [form, setForm] = useState({
    givenName: '',
    surName: '',
    email: '',
    postalCode: '',
    acceptTerms: true,
    phoneNumber: '5555555555',
    country: 'US',
  });
  const [utmParams, setUtmParams] = useState({});
  const [cardValid, setCardValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const recurlyPlanCode = 'jb-m-coo-ft-pro-limited';
  const testApplePayPlancode = 'jb-apple-pay-testing';
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  // Extraer UTM params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mapping = {
      first_name: 'givenName',
      last_name: 'surName',
      email: 'email',
    };
    const newForm = { ...form };
    Object.entries(mapping).forEach(([key, field]) => {
      if (params.has(key)) newForm[field] = params.get(key);
    });
    setForm(newForm);
    setUtmParams({
      utmAdGroup: params.get('utm_adgroup') || '',
      utmCampaign: params.get('utm_campaign') || '',
      utmContent: params.get('utm_content') || '',
      utmDevice: params.get('utm_device') || '',
      utmKeyword: params.get('utm_keyword') || '',
      utmMatchType: params.get('utm_matchtype') || '',
      utmMedium: params.get('utm_medium') || '',
      utmNetwork: params.get('utm_network') || '',
      utmPlacement: params.get('utm_placement') || '',
      utmSource: params.get('utm_source') || '',
    });
  }, []);

  // Cargar datos del plan
  useEffect(() => {
    getRecurlyPlanOpen(recurlyPlanCode).then((res) => setPlanData(res));
  }, []);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const isValid = () => {
    if (!planData) return false;
    const basic = form.givenName && form.surName && form.email;
    if (planData.isFreemium) return basic;
    return basic && form.postalCode && cardValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const recaptchaToken = await executeRecaptcha('signUp');
      const isApplePay = e.nativeEvent.submitter.id === 'applePay';

      const recurlyForm = {
        first_name: form.givenName,
        last_name: form.surName,
        postal_code: form.postalCode,
        country: 'US',
      };

      if (isApplePay) {
        // Apple Pay flow
        const applePay = recurly.ApplePay({
          country: 'US',
          currency: 'USD',
          label: 'Advisory Cloud',
          total: '0.01',
          callbacks: {
            onPaymentAuthorized: (event) => {
              console.log('Payment Authorized:', event);
            },
          },
          braintree: {
            clientAuthorization: 'merchant.SandBox.AdvisoryCloud',
          },
        });
      } else {
        // Card flow
        recurly.token(recurlyForm, async (err, token) => {
          if (err) throw err;
          const res = await signUp({
            ...form,
            ...utmParams,
            companyName: `${form.givenName} ${form.surName}`,
            phoneCountryCode: 'US',
            recurlyBillingToken: token.id,
            recurlyPlanCode: testApplePayPlancode,
            urlOfCheckoutPage: window.location.href,
            acceptTermsOfService: form.acceptTerms,
            recaptchaToken,
          });
          if (res.errors?.length) {
            const msg = JSON.parse(res.errors[0].message).error.message;
            setError(msg || 'Something went wrong');
            setLoading(false);
            return;
          }
          const { checkoutRedirectUrl, accountType } = res;
          if (checkoutRedirectUrl) {
            window.location.assign(
              checkoutRedirectUrl + window.location.search
            );
          } else {
            window.location.assign(
              `/${
                accountType === 'B2B' ? 'company' : 'advisor'
              }/confirmation?email=${form.email}`
            );
          }
        });
      }
    } catch (err) {
      setError(err.message || 'Something went wrong');
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Logo centrado */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: '-30px',
          mb: { xs: -3, xxs: -5 },
          // borderBottom: '1px solid #d2c02a',
          // boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <AdvisoryCloudLogo removeHyperlink width={isXS ? 180 : 185} />
      </Box>
      <StepsBanner />

      <Grid container spacing={6} sx={{ px: 5 }}>
        {/* LADO IZQUIERDO */}
        <Grid item xxs={12} md={6} sx={{ p: { xxs: '0px', xs: '100px' } }}>
          <Typography
            sx={{
              fontSize: { xxs: '25px', xs: '48px' },
              fontWeight: 800,
              mb: 3,
            }}
          >
            You have{' '}
            <Box component="span" sx={{ color: 'rgb(20,120,248)' }}>
              74+
            </Box>{' '}
            potential board matches!
          </Typography>

          {/* Aquí iría la imagen del mockup (laptop + móvil) */}
          <Box sx={{ width: '100%', borderRadius: 2, mb: 4 }}>
            <img
              src={screen}
              alt="Checkout Mockup"
              style={{ width: '100%', borderRadius: '16px' }}
            />
          </Box>
          {bullets.map((txt, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                mb: 2,
                bgcolor: '#f1f9ff',
                borderRadius: '8px',
                p: 2,
                alignItems: 'center',
              }}
            >
              <Box
                component="span"
                sx={{
                  minWidth: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'rgb(0, 84, 139)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '1rem',
                  alignSelf: 'center',
                }}
              >
                {i + 1}
              </Box>

              {/* Línea vertical */}
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  mx: 2,
                  width: 2,
                  bgcolor: 'rgb(0, 84, 139)',
                }}
              />

              {/* Texto */}
              <Typography
                sx={{
                  color: 'rgb(0, 84, 139)',
                  fontSize: { xs: '14px', md: '18px' },
                  lineHeight: 1.4,
                }}
              >
                {txt}
              </Typography>
            </Box>
          ))}
        </Grid>

        {/* LADO DERECHO */}
        <Grid item xxs={12} md={6} sx={{ p: { xxs: '0px', xs: '100px' } }}>
          {/* Banner azul */}
          <Box
            sx={{
              bgcolor: 'rgba(43,113,246,1)',
              color: 'white',
              p: 2,
              // borderRadius: 1,
              textAlign: 'center',
              mb: 2,
              fontSize: '20px',
              fontWeight: 800,
            }}
          >
            See why over 25,000+ Directors, VPs &amp; CXOs are on AdvisoryCloud.
          </Box>

          <TrustPilotBanner />

          <Typography sx={{ mt: 3, mb: 2, fontSize: '20px' }}>
            <strong>14-Day Free Trial</strong>, then just $195/month. Cancel
            anytime.
          </Typography>

          {/* Formulario */}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xxs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={form.givenName}
                  onChange={handleChange('givenName')}
                />
              </Grid>
              <Grid item xxs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={form.surName}
                  onChange={handleChange('surName')}
                />
              </Grid>
              <Grid item xxs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={form.email}
                  onChange={handleChange('email')}
                />
              </Grid>

              {planData?.accountType === 'B2C' && !planData.isFreemium && (
                <>
                  <Grid item xxs={12}>
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            color: '#32325d',
                            '::placeholder': { color: '#aab7c4' },
                          },
                          invalid: { color: '#fa755a' },
                        },
                      }}
                      onChange={(e) => setCardValid(e.valid)}
                    />
                  </Grid>
                  <Grid item xxs={12}>
                    <TextField
                      fullWidth
                      label="Billing Zip Code"
                      value={form.postalCode}
                      onChange={handleChange('postalCode')}
                    />
                  </Grid>
                </>
              )}

              {error && (
                <Grid item xxs={12}>
                  <Typography color="error">{error}</Typography>
                </Grid>
              )}

              <Grid item xxs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    bgcolor: 'rgb(8,114,253)',
                    color: 'white',
                    borderRadius: '40px',
                    fontSize: '18px',
                    fontWeight: 700,
                    py: 1.5,
                    '&:disabled': {
                      bgcolor: 'rgb(8,114,253)',
                      color: 'white',
                      cursor: 'not-allowed',
                      opacity: 0.5,
                    },
                  }}
                  disabled={!isValid() || loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    'Start Your Free Trial'
                  )}
                </Button>
              </Grid>
              <Grid item xxs={12}>
                <Box sx={{ my: 2 }}>
                  <Divider textAlign="center">
                    <Typography component="span" sx={{ fontSize: '0.875rem' }}>
                      or use Express Checkout
                    </Typography>
                  </Divider>
                </Box>
              </Grid>

              {/* Apple Pay */}
              <Grid item xxs={12}>
                <Button
                  type="submit"
                  id="applePay"
                  variant="contained"
                  fullWidth
                  sx={{
                    bgcolor: 'black',
                    color: 'white',
                    borderRadius: '40px',
                    fontSize: '18px',
                    fontWeight: 700,
                    py: 1.5,
                    '&:disabled': { opacity: 0.5 },
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <>
                      Buy with&nbsp;
                      <AppleIcon
                        sx={{ fontSize: 24, verticalAlign: 'middle' }}
                      />
                      &nbsp;Pay
                    </>
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Disclaimers */}
          <Box sx={{ m: 3 }}>
            <Typography sx={{ display: 'flex', mb: 1, fontSize: '18px' }}>
              <Box component="span" sx={{ mr: 1 }}>
                ✓
              </Box>
              We will NOT bill your credit card for 14 days. Guaranteed.
            </Typography>
            <Typography sx={{ display: 'flex', mb: 1, fontSize: '18px' }}>
              <Box component="span" sx={{ mr: 1 }}>
                ✓
              </Box>
              After 14 days your membership renews monthly at $195.
            </Typography>
            <Typography
              sx={{
                display: 'flex',
                flexWrap: 'wrap', // <-- aquí permites que los items salten de línea
                alignItems: 'center',
                mb: 1,
                fontSize: '18px',
              }}
            >
              ✓ By starting your free trial, you agree to our &nbsp;{' '}
              <Link
                href="https://www.advisorycloud.com/terms-of-service"
                target="_blank"
                rel="noopener"
                sx={{ whiteSpace: 'nowrap' }} // opcional: evita que “Terms & Privacy” se parta por la mitad
              >
                Terms & Privacy
              </Link>
              .
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={4} sx={{ p: 3 }}>
        {testimonials.map((t, i) => (
          <Grid item xxs={12} sm={6} key={i}>
            <TestimonialCard
              key={i}
              description={t.description}
              name={t.name}
              title={t.title}
              image={t.image}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CheckoutMatch;
